// #region angular Imports
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from "@angular/common/http";
import { NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { RouterModule } from "@angular/router";
import { JwtModule } from "@auth0/angular-jwt";
import { NgbDateNativeAdapter, NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { environment } from 'environments/environment';
// #endregion

// #region app Imports
import { CiInvoicePdfComponent } from "app/ci-repack/ci-invoices/invoice-pdf/invoice-pdf.component";
import { CiRepackOrderShortOverPrintableComponent } from 'app/ci-repack/ci-repack-order/ci-repack-order-short-over/ci-repack-order-short-over-printable/ci-repack-order-short-over-printable.component';
import { BARCODE_PARSE_PROVIDER } from "app/shared/barcode-scanner/barcode-parse-provider";
import { BarcodeParseApiService } from "app/shared/services/barcode-parse-api.service";
import { NgbDateNativeParserFormatter } from "app/shared/form-elements/components/date-picker-parser/ngb-date-native-parser-formatter";
import { NavigationService } from "app/shared/navigation/navigation.service";
import { QuickUserInfoComponent } from 'app/shared/quick-user-info/quick-user-info.component';
import { SharedModule } from "app/shared/shared.module";
import { FilterSaveService } from "app/shared/table/services/filter-save.service";
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha-2';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { CollapsibleTableService } from './collapsible-table/collapsible-table.service';
import { CoreRouting } from "./core.routing";
import { InactivityTimeoutComponent } from './inactivity-timeout/inactivity-timeout.component';
import { InactivityTimeoutService } from "./inactivity-timeout/inactivity-timeout.service";
import { HttpClientInterceptor } from "./interceptors/http-client-interceptor";
import { TutorialHttpGuardInterceptor } from "./interceptors/tutorial-http-guard.interceptor";
import { LayoutModule } from "./layout/layout.module";
import { SpinnerService } from "./layout/spinner/spinner.service";
import { DashboardNotificationsWidgetComponent } from './pages/dashboard/widgets/notifications-widget/notifications-widget.component';
import { HelpDeskTicketFeedbackComponent } from './pages/help-desk-ticket-feedback/help-desk-ticket-feedback.component';
import { PublicHelpTicketComponent } from "./pages/help-ticket/help-ticket.component";
import { PublicRegisterComponent } from "./pages/public-register/public-register.component";
import { HerosApplicationComponent } from "./pages/heros-application/heros-application.component";
import { HolidaysComponent } from "./pages/holidays/holidays.component";
import { HomeDeliverySubmitModalComponent } from "./pages/home-delivery/home-delivery-submit-modal/home-delivery-submit-modal.component";
import { HomeDeliveryComponent } from "./pages/home-delivery/home-delivery.component";
import { HomerRegisterComponent } from "./pages/homer-register/homer-register.component";
import { IcqFeedbackComponent } from "./pages/icq-feedback/icq-feedback.component";
import { IcqSchedulingComponent } from "./pages/icq-scheduling/icq-scheduling.component";
import { MultiFactorAuthModalComponent } from "./pages/login/multi-factor-auth-modal/multi-factor-auth-modal.component";
import { MobileScanningInterceptor } from "./pages/mobile-scanning/mobile-scanning.interceptor";
import { PfsRegisterComponent } from "./pages/pfs-register/pfs-register.component";
import { DonorRegistrationComponent } from "./pages/rxconnects/donor/donor-registration.component";
import { MetaPixelComponent } from "./pages/rxconnects/meta-pixel/meta-pixel.component";
import { PatientApplicationComponent } from "./pages/rxconnects/patient-application/patient-application.component";
import { TechSpecComponent } from "./pages/tech-spec/tech-spec.component";
import { WebReleaseNotesComponent } from "./pages/web-release-notes/web-release-notes.component";
import { PushNotificationModule } from "./push-notification/push-notification.module";
import { GetPasswordComponent } from "./security/get-password/get-password.component";
import { WebAuthnService } from "./security/multi-factor-auth/web-authn.service";
import { SecurityService } from "./security/security.service";
import { SendgridApiService } from "./sendgrid-api/sendgrid-api.service";
import { SseNotificationModule } from "./sse-notification/sse-notification.module";
import { SystemMessageService } from "./system-message/system-message-service";
import { CustomWidgetDashboard } from "./pages/dashboard/custom-widget-dashboard/custom-widget-dashboard.component";

export function getToken() { return localStorage.getItem("jwt"); }

/***
 * Contains application-wide, single use components and services.
 * Imported once (in the AppModule) when the app starts and never imported anywhere else.
*/
@NgModule({
    declarations: [
        ...CoreRouting.declarations
        , MultiFactorAuthModalComponent
        , GetPasswordComponent
        , HerosApplicationComponent
        , WebReleaseNotesComponent
        , HomeDeliveryComponent
        , HomeDeliverySubmitModalComponent
        , TechSpecComponent
        , HolidaysComponent
        , PfsRegisterComponent
        , HomerRegisterComponent
        , PublicHelpTicketComponent
        , DonorRegistrationComponent
        , PatientApplicationComponent
        , MetaPixelComponent
        , CiInvoicePdfComponent
        , CiRepackOrderShortOverPrintableComponent
        , HelpDeskTicketFeedbackComponent
        , IcqSchedulingComponent
        , InactivityTimeoutComponent
        , IcqFeedbackComponent
    ]
    , imports: [
        ...CoreRouting.imports
        , BrowserModule
        , BrowserAnimationsModule
        , PushNotificationModule
        , SseNotificationModule
        , LayoutModule
        , SharedModule
        , JwtModule.forRoot({
            config: {
                tokenGetter: getToken
                , allowedDomains: [ globalThis.location.host ]
            }
        })
        , RecaptchaV3Module
        , DashboardNotificationsWidgetComponent
        , QuickUserInfoComponent
        , CustomWidgetDashboard
        , PublicRegisterComponent
        , ButtonModule
        , InputTextModule
        , PasswordModule
        , FloatLabelModule
        , IconFieldModule
        , InputIconModule
    ]
    , exports: [
        ...CoreRouting.exports
        , SharedModule
        , LayoutModule
        , RouterModule
        , PushNotificationModule
        , SseNotificationModule
        , BrowserModule
    ]
    , providers: [
        ...CoreRouting.providers
        , SystemMessageService
        , SpinnerService
        , SecurityService
        , WebAuthnService
        , InactivityTimeoutService
        , CollapsibleTableService
        , NavigationService
        , FilterSaveService
        , SendgridApiService
        , {
            provide: NgbDateParserFormatter
            , useClass: NgbDateNativeParserFormatter
        }
        , NgbDateNativeAdapter
        , {
            // Point the shared scanner's parse-provider seam at the real backend parse service, so any
            // <pcg-barcode-scanner [serverParse]="true"> reaches api/EpcisBarcode/parse.
            provide: BARCODE_PARSE_PROVIDER
            , useExisting: BarcodeParseApiService
        }
        , {
            // First, so a request refused during a simulated tour never reaches the spinner or URL rewriting.
            provide: HTTP_INTERCEPTORS
            , useClass: TutorialHttpGuardInterceptor
            , multi: true
        }
        , {
            provide: HTTP_INTERCEPTORS
            , useClass: MobileScanningInterceptor
            , multi: true
        }
        , {
            provide: HTTP_INTERCEPTORS
            , useClass: HttpClientInterceptor
            , multi: true
        }
        , { 
            provide: RECAPTCHA_V3_SITE_KEY
            , useValue: environment.siteKey 
        }
        , provideHttpClient(withXhr(), withInterceptorsFromDi())
    ]
})
export class CoreModule {}
